import React, {useState, useEffect, useMemo} from 'react';
import {useNavigate, useParams, useLocation} from 'react-router-dom';
import Box from "@mui/joy/Box";
import Typography from "@mui/joy/Typography";
import Button from "@mui/joy/Button";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import Input from "@mui/joy/Input";
import Select from "@mui/joy/Select";
import Option from "@mui/joy/Option";
import Stack from "@mui/joy/Stack";
import Switch from "@mui/joy/Switch";
import {PaymentMethodModel, PaymentOperationType, PaymentSystemType, PaymentExtraFields, getPaymentExtraFieldLabel} from "../../../models/paymentMethod";
import {PaymentMethodsApi} from "./paymentMethodsApi";
import {NotificationService} from "../../../common/notification";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import useMediaQuery from '@mui/material/useMediaQuery';
import {useTheme} from '@mui/joy/styles';
import ImageUploading, {ImageListType} from 'react-images-uploading';
import IconButton from "@mui/joy/IconButton";
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import Chip from "@mui/joy/Chip";
import { kopecksToRublesNumber, rublesToKopecksNumber } from '../../../common/money';

export function PaymentMethodEditor() {
    const navigate = useNavigate();
    const {id} = useParams();
    const location = useLocation() as { state?: { method?: PaymentMethodModel } };
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isEditMode = Boolean(id);

    const [formData, setFormData] = useState<Partial<PaymentMethodModel>>({
        operationType: PaymentOperationType.Deposit,
        systemType: PaymentSystemType.Tome,
        titleRu: '',
        titleEn: '',
        image: '',
        currencies: [],
        sort: 0,
        minAmount: 0,
        maxAmount: 0,
        ourCommissionPercent: 0,
        providerFixedCommission: 0,
        providerCommissionPercent: 0,
        providerMinCommission: 0,
        externalRef: '',
        isActive: false,
        requiredFields: PaymentExtraFields.None,
        calculateWithdrawalTime: false,
    });

    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(isEditMode);
    const [minAmountRub, setMinAmountRub] = useState<number>(0);
    const [maxAmountRub, setMaxAmountRub] = useState<number>(0);
    const [providerFixedCommission, setProviderFixedCommission] = useState<number>(0);
    const [providerMinCommissionRub, setProviderMinCommissionRub] = useState<number>(0);
    const [image, setImage] = useState<ImageListType>([]);

    // Инициализация данных для редактирования: сначала из props (location.state), затем при отсутствии — с сервера
    useEffect(() => {
        if (isEditMode && id) {
            const fromState = location?.state?.method;
            if (fromState && fromState.id === parseInt(id)) {
                setFormData(fromState);
                setMinAmountRub(Number(kopecksToRublesNumber(fromState.minAmount).toFixed(2)));
                setMaxAmountRub(Number(kopecksToRublesNumber(fromState.maxAmount).toFixed(2)));
                setProviderFixedCommission(Number(kopecksToRublesNumber(fromState.providerFixedCommission).toFixed(2)));
                setProviderMinCommissionRub(Number(kopecksToRublesNumber(fromState.providerMinCommission || 0).toFixed(2)));
                setInitialLoading(false);
                return;
            }
            loadPaymentMethod(parseInt(id));
        } else {
            // режим создания — нет начальной загрузки
            setInitialLoading(false);
        }
    }, [id, isEditMode]);

    const loadPaymentMethod = async (methodId: number) => {
        try {
            const method = await PaymentMethodsApi.getPaymentMethod(methodId);
            if (method) {
                setFormData(method);
                setMinAmountRub(Number(kopecksToRublesNumber(method.minAmount).toFixed(2)));
                setMaxAmountRub(Number(kopecksToRublesNumber(method.maxAmount).toFixed(2)));
                setProviderFixedCommission(Number(kopecksToRublesNumber(method.providerFixedCommission).toFixed(2)));
                setProviderMinCommissionRub(Number(kopecksToRublesNumber(method.providerMinCommission || 0).toFixed(2)));
            } else {
                NotificationService.alertError("Метод оплаты не найден");
                navigate(-1);
            }
        } catch (error) {
            console.error('Error loading payment method:', error);
            NotificationService.alertError("Ошибка загрузки метода оплаты");
            navigate(-1);
        } finally {
            setInitialLoading(false);
        }
    };

    const handleInputChange = (field: keyof PaymentMethodModel, value: any) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const addCurrency = (cur: string) => {
        const val = cur.trim().toUpperCase();
        if (!val) return;
        setFormData(prev => ({
            ...prev,
            currencies: Array.from(new Set([...(prev.currencies ?? []), val]))
        }));
    };

    const removeCurrencyItem = (cur: string) => {
        setFormData(prev => ({
            ...prev,
            currencies: (prev.currencies ?? []).filter(c => c !== cur)
        }));
    };

    const handleCurrencyKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
        if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
            e.preventDefault();
            const target = e.target as HTMLInputElement;
            addCurrency(target.value);
            target.value = '';
        }
    };


    const handleSubmit = async () => {
        if (!formData.titleRu || !formData.currencies || formData.currencies.length === 0) {
            NotificationService.alertError("Заполните обязательные поля");
            return;
        }

        // Бизнес-валидация: активный метод должен иметь внешний идентификатор
        if (formData.isActive && (!formData.externalRef || formData.externalRef.trim() === '')) {
            NotificationService.alertError("Для активного метода необходимо указать внешний ID");
            return;
        }

        // Валидация сумм в рублях
        if ((minAmountRub || 0) <= 0 || (maxAmountRub || 0) <= 0) {
            NotificationService.alertError("Минимальная и максимальная суммы должны быть больше 0");
            return;
        }
        if ((maxAmountRub || 0) < (minAmountRub || 0)) {
            NotificationService.alertError("Максимальная сумма не может быть меньше минимальной");
            return;
        }

        setLoading(true);
        try {
            if (isEditMode && id) {
                // Редактирование существующего метода (multipart/form-data)
                const ok = await PaymentMethodsApi.updatePaymentMethodForm({
                    ...formData,
                    id: parseInt(id),
                    minAmount: rublesToKopecksNumber(minAmountRub || 0),
                    maxAmount: rublesToKopecksNumber(maxAmountRub || 0),
                    providerFixedCommission: rublesToKopecksNumber(providerFixedCommission || 0),
                    providerMinCommission: rublesToKopecksNumber(providerMinCommissionRub || 0),
                }, image.length === 1 ? image[0].file : undefined);

                if (ok) {
                    NotificationService.alertSuccess("Метод оплаты успешно обновлен");
                    navigate(-1);
                } else {
                    throw new Error('Ошибка обновления');
                }
            } else {
                // Создание нового метода (multipart/form-data)
                const ok = await PaymentMethodsApi.createPaymentMethodForm({
                    ...formData,
                    minAmount: rublesToKopecksNumber(minAmountRub || 0),
                    maxAmount: rublesToKopecksNumber(maxAmountRub || 0),
                    providerFixedCommission: rublesToKopecksNumber(providerFixedCommission || 0),
                    providerMinCommission: rublesToKopecksNumber(providerMinCommissionRub || 0),
                }, image.length === 1 ? image[0].file : undefined);

                if (ok) {
                    NotificationService.alertSuccess("Метод оплаты успешно создан");
                    navigate(-1);
                } else {
                    throw new Error('Ошибка создания');
                }
            }
        } catch (error) {
            console.error('Error saving payment method:', error);
            NotificationService.alertError("Ошибка сохранения метода оплаты");
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        navigate(-1);
    };

    const headerTitle = useMemo(() => (isEditMode ? 'Редактировать метод оплаты' : 'Добавить новый метод оплаты'), [isEditMode]);

    if (initialLoading) {
        return (
            <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh'}}>
                <Typography>Загрузка...</Typography>
            </Box>
        );
    }

    return (
        <Box
            component="main"
            className="MainContent"
            sx={{
                px: {xs: 2, md: 6},
                pt: {
                    xs: 'calc(12px + var(--Header-height))',
                    sm: 'calc(12px + var(--Header-height))',
                    md: 3,
                },
                pb: {xs: 2, sm: 2, md: 3},
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minWidth: 0,
                height: '100dvh',
                gap: 2,
            }}
        >
            {/* Заголовок */}
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                mb: 2
            }}>
                <Button
                    variant="plain"
                    startDecorator={<ArrowBackIcon/>}
                    onClick={handleCancel}
                    sx={{minWidth: 'auto'}}
                >
                    {isMobile ? '' : 'Назад'}
                </Button>
                <Typography level="h2" component="h1">{headerTitle}</Typography>
            </Box>

            {/* Форма */}
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
                maxWidth: isMobile ? '100%' : 800,
                width: '100%'
            }}>
                {/* Основная информация */}
                <Box sx={{
                    bgcolor: 'background.level1',
                    borderRadius: 8,
                    p: 3
                }}>
                    <Typography level="h3" sx={{mb: 2}}>
                        Основная информация
                    </Typography>
                    
                    <Stack spacing={3}>
                        {/* Статус и переключатель */}
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2
                        }}>
                            <Switch
                                checked={!!formData.isActive}
                                onChange={(e) => handleInputChange('isActive', e.target.checked)}
                                color={formData.isActive ? 'success' : 'neutral'}
                                slotProps={{ track: { sx: { minWidth: 44 } } }}
                            />
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: formData.isActive ? 'success.500' : 'danger.500' }} />
                                <Typography level="body-sm" color={formData.isActive ? 'success' : 'danger'}>
                                    {formData.isActive ? 'Активен' : 'Отключен'}
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                            gap: 2
                        }}>
                            <FormControl required>
                                <FormLabel>Название (RU)</FormLabel>
                                <Input
                                    value={formData.titleRu || ''}
                                    onChange={(e) => handleInputChange('titleRu', e.target.value)}
                                    placeholder="Введите название на русском"
                                />
                            </FormControl>

                            <FormControl>
                                <FormLabel>Название (EN)</FormLabel>
                                <Input
                                    value={formData.titleEn || ''}
                                    onChange={(e) => handleInputChange('titleEn', e.target.value)}
                                    placeholder="Введите название на английском"
                                />
                            </FormControl>
                        </Box>

                        <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                            gap: 2
                        }}>
                            <FormControl required>
                                <FormLabel>Тип операции</FormLabel>
                                <Select
                                    value={formData.operationType || PaymentOperationType.Deposit}
                                    onChange={(_, value) => handleInputChange('operationType', value)}
                                >
                                    <Option value={PaymentOperationType.Deposit}>Пополнение</Option>
                                    <Option value={PaymentOperationType.Withdraw}>Вывод</Option>
                                </Select>
                            </FormControl>

                            <FormControl required>
                                <FormLabel>Тип системы</FormLabel>
                                <Select
                                    value={formData.systemType || PaymentSystemType.Tome}
                                    onChange={(_, value) => handleInputChange('systemType', value)}
                                >
                                    <Option value={PaymentSystemType.Tome}>Tome</Option>
                                    <Option value={PaymentSystemType.GameMoney}>GameMoney</Option>
                                </Select>
                            </FormControl>
                        </Box>

                        <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                            gap: 2
                        }}>
                            <FormControl required>
                                <FormLabel>Валюты</FormLabel>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                        {(formData.currencies ?? []).map(c => (
                                            <Chip key={c} variant="soft" size="sm" color="primary" onClick={() => removeCurrencyItem(c)} sx={{ cursor: 'pointer' }}>
                                                {c} ×
                                            </Chip>
                                        ))}
                                    </Box>
                                    <Input
                                        placeholder="Добавьте валюту и нажмите Enter (например, RUB)"
                                        onKeyDown={handleCurrencyKeyDown}
                                    />
                                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                        {['RUB', 'USD', 'EUR', 'KZT', 'UAH', 'BYN', 'TRY'].map(c => (
                                            <Chip key={c} size="sm" variant="soft" onClick={() => addCurrency(c)} sx={{ cursor: 'pointer' }}>{c}</Chip>
                                        ))}
                                    </Box>
                                </Box>
                            </FormControl>

                            <FormControl required>
                                <FormLabel>Сортировка</FormLabel>
                                <Input
                                    type="number"
                                    value={formData.sort || 0}
                                    onChange={(e) => handleInputChange('sort', parseInt(e.target.value) || 0)}
                                    placeholder="0"
                                />
                            </FormControl>
                        </Box>

                        <FormControl>
                            <FormLabel>Логотип</FormLabel>
                            <ImageUploading value={image} onChange={(e) => setImage(e)} maxNumber={1}>
                                {({ onImageUpload, onImageUpdate }) => (
                                    <div className="upload__image-wrapper">
                                        <Stack direction={'row'} spacing={1} alignItems={'center'}>
                                            {(image.length === 1 || formData.image) && (
                                                <img src={image.length === 1 ? image[0].dataURL! : formData.image!}
                                                     alt="logo" width={50} height={50} />
                                            )}
                                            <IconButton color="primary"
                                                onClick={() => image.length === 1 ? onImageUpdate(0) : onImageUpload()}>
                                                <AddPhotoAlternateIcon/>
                                            </IconButton>
                                        </Stack>
                                    </div>
                                )}
                            </ImageUploading>
                        </FormControl>
                    </Stack>
                </Box>

                {/* Суммы и комиссии */}
                <Box sx={{
                    bgcolor: 'background.level1',
                    borderRadius: 8,
                    p: 3
                }}>
                    <Typography level="h3" sx={{mb: 2}}>
                        Суммы и комиссии
                    </Typography>
                    
                    <Stack spacing={3}>
                        <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                            gap: 2
                        }}>
                            <FormControl required>
                                <FormLabel>Минимальная сумма (в рублях, долларах, евро итд)</FormLabel>
                                <Input
                                    type="number"
                                    value={minAmountRub}
                                    onChange={(e) => setMinAmountRub(parseFloat(e.target.value) || 0)}
                                    placeholder="100.00"
                                    slotProps={{ input: { step: '0.01' } }}
                                />
                            </FormControl>

                            <FormControl required>
                                <FormLabel>Максимальная сумма (в рублях, долларах, евро итд)</FormLabel>
                                <Input
                                    type="number"
                                    value={maxAmountRub}
                                    onChange={(e) => setMaxAmountRub(parseFloat(e.target.value) || 0)}
                                    placeholder="1000.00"
                                    slotProps={{ input: { step: '0.01' } }}
                                />
                            </FormControl>
                        </Box>

                        <FormControl required>
                            <FormLabel>Наша комиссия (%)</FormLabel>
                            <Input
                                type="number"
                                value={formData.ourCommissionPercent || 0}
                                onChange={(e) => handleInputChange('ourCommissionPercent', parseFloat(e.target.value) || 0)}
                                placeholder="2.5"
                                slotProps={{ input: { step: '0.01' } }}
                            />
                        </FormControl>

                        <Box>
                            <Typography level="title-sm" sx={{ mb: 2 }}>
                                Комиссия провайдера
                            </Typography>
                            <Box sx={{
                                display: 'grid',
                                gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr',
                                gap: 2
                            }}>
                                <FormControl required>
                                    <FormLabel>Процент (%)</FormLabel>
                                    <Input
                                        type="number"
                                        value={formData.providerCommissionPercent || 0}
                                        onChange={(e) => handleInputChange('providerCommissionPercent', parseFloat(e.target.value) || 0)}
                                        placeholder="1.0"
                                        slotProps={{ input: { step: '0.01' } }}
                                    />
                                </FormControl>
                                <FormControl required>
                                    <FormLabel>Фиксированная</FormLabel>
                                    <Input
                                        type="number"
                                        value={providerFixedCommission}
                                        onChange={(e) => setProviderFixedCommission(parseFloat(e.target.value) || 0)}
                                        placeholder="10.00"
                                        slotProps={{ input: { step: '0.01' } }}
                                    />
                                </FormControl>
                                <FormControl>
                                    <FormLabel>Минимальная (руб.)</FormLabel>
                                    <Input
                                        type="number"
                                        value={providerMinCommissionRub}
                                        onChange={(e) => setProviderMinCommissionRub(parseFloat(e.target.value) || 0)}
                                        placeholder="0.00"
                                        slotProps={{ input: { step: '0.01' } }}
                                    />
                                </FormControl>
                            </Box>
                        </Box>
                    </Stack>
                </Box>

                {/* Дополнительная информация */}
                <Box sx={{
                    bgcolor: 'background.level1',
                    borderRadius: 8,
                    p: 3
                }}>
                    <Typography level="h3" sx={{mb: 2}}>
                        Дополнительная информация
                    </Typography>
                    
                    <Stack spacing={3}>
                        <FormControl>
                            <FormLabel>Внешний ID</FormLabel>
                            <Input
                                value={formData.externalRef || ''}
                                onChange={(e) => handleInputChange('externalRef', e.target.value)}
                                placeholder="Введите внешний идентификатор"
                            />
                        </FormControl>

                        <FormControl>
                            <FormLabel>Тег (RU)</FormLabel>
                            <Input
                                value={formData.tagRu || ''}
                                onChange={(e) => handleInputChange('tagRu', e.target.value)}
                                placeholder="Опциональный тег на русском"
                            />
                        </FormControl>

                        <FormControl>
                            <FormLabel>Тег (EN)</FormLabel>
                            <Input
                                value={formData.tagEn || ''}
                                onChange={(e) => handleInputChange('tagEn', e.target.value)}
                                placeholder="Опциональный тег на английском"
                            />
                        </FormControl>

                        <Box>
                            <FormLabel>Требуемые поля</FormLabel>
                            <Stack spacing={1}>
                                {Object.entries(PaymentExtraFields)
                                    .filter(([key, value]) => typeof value === 'number' && value !== PaymentExtraFields.None)
                                    .map(([key, value]) => (
                                        <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Switch
                                                checked={Boolean(formData.requiredFields && (formData.requiredFields & (value as number)))}
                                                onChange={(e) => {
                                                    const currentFields = (formData.requiredFields as number) || PaymentExtraFields.None;
                                                    const newFields = e.target.checked
                                                        ? currentFields | (value as number)
                                                        : currentFields & ~(value as number);
                                                    handleInputChange('requiredFields', newFields);
                                                }}
                                                size="sm"
                                            />
                                            <Typography level="body-sm">{getPaymentExtraFieldLabel(key)}</Typography>
                                        </Box>
                                    ))}
                            </Stack>
                        </Box>

                        {formData.operationType === PaymentOperationType.Withdraw && (
                            <FormControl>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Switch
                                        checked={!!formData.calculateWithdrawalTime}
                                        onChange={(e) => handleInputChange('calculateWithdrawalTime', e.target.checked)}
                                        color={formData.calculateWithdrawalTime ? 'success' : 'neutral'}
                                        slotProps={{ track: { sx: { minWidth: 44 } } }}
                                    />
                                    <Box>
                                        <FormLabel>Рассчитывать статистику скорости выводов</FormLabel>
                                        <Typography level="body-xs" color="neutral">
                                            Включить расчет статистики скорости выводов для данного метода
                                        </Typography>
                                    </Box>
                                </Box>
                            </FormControl>
                        )}
                    </Stack>
                </Box>

                {/* Кнопки действий */}
                <Box sx={{
                    display: 'flex',
                    gap: 2,
                    justifyContent: 'flex-end',
                    flexDirection: isMobile ? 'column' : 'row'
                }}>
                    <Button
                        variant="outlined"
                        onClick={handleCancel}
                        sx={{minWidth: isMobile ? '100%' : 120}}
                    >
                        Отмена
                    </Button>
                    <Button
                        variant="solid"
                        startDecorator={<SaveIcon/>}
                        onClick={handleSubmit}
                        loading={loading}
                        sx={{minWidth: isMobile ? '100%' : 120}}
                    >
                        {isEditMode ? 'Сохранить' : 'Создать'}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
}
